import {useEffect, useMemo, useState} from "react";
import {Download, FileCheck2, FileWarning, LoaderCircle} from "lucide-react";
import {formatDateTime} from "../../../shared/utils/format";
import {downloadDocument, mapOnboardingError} from "../api";
import {useTranslation, i18n} from "../../../shared/i18n";


const DOCUMENT_TYPE_LABELS = {
    get bank_details() { return i18n.t("sections.onboarding.p91"); },
    get registration() { return i18n.t("sections.onboarding.p95"); },
    get director_identity() { return i18n.t("sections.onboarding.p96"); },
    get premises() { return i18n.t("sections.onboarding.p97"); },
};

const DOCUMENT_STATUS_LABELS = {
    get pending() { return i18n.t("sections.onboarding.p98"); },
    get accepted() { return i18n.t("sections.onboarding.p99"); },
    get rejected() { return i18n.t("sections.onboarding.p100"); },
};

const formatBytes = (value) => {
    if (value < 1024) return i18n.t("sections.onboarding.p101", {v0: value});
    if (value < 1024 * 1024) return i18n.t("sections.onboarding.p102", {v0: new Intl.NumberFormat("ru-RU", {maximumFractionDigits: 1}).format(value / 1024)});
    return i18n.t("sections.onboarding.p103", {v0: new Intl.NumberFormat("ru-RU", {maximumFractionDigits: 1}).format(value / (1024 * 1024))});
};

export const DocumentList = ({applicationId, countryScope, documents, onForbidden}) => {
    const {t} = useTranslation();
    const [downloadState, setDownloadState] = useState({});
    const [now, setNow] = useState(Date.now());
    const hasActiveWait = useMemo(
        () =>
            Object.values(downloadState).some(
                (state) => state.retryAvailableAt && state.retryAvailableAt > now,
            ),
        [downloadState, now],
    );

    useEffect(() => {
        if (!hasActiveWait) return undefined;
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [hasActiveWait]);

    const startDownload = async (documentRow) => {
        setDownloadState((value) => ({
            ...value,
            [documentRow.id]: {status: "loading", message: "", retryAvailableAt: null},
        }));
        let objectUrl = null;
        try {
            const result = await downloadDocument({
                applicationId,
                documentId: documentRow.id,
                countryScope,
            });
            objectUrl = URL.createObjectURL(result.blob);
            const anchor = window.document.createElement("a");
            anchor.href = objectUrl;
            anchor.download = result.filename;
            anchor.click();
            setDownloadState((value) => ({
                ...value,
                [documentRow.id]: {status: "idle", message: "", retryAvailableAt: null},
            }));
        } catch (error) {
            const mapped = mapOnboardingError(error);
            if (mapped.kind === "forbidden") {
                setDownloadState({});
                onForbidden?.(mapped);
                return;
            }
            const retryAvailableAt =
                mapped.kind === "rate_limited" && mapped.retryAfterSeconds !== null
                    ? Date.now() + mapped.retryAfterSeconds * 1000
                    : null;
            setNow(Date.now());
            setDownloadState((value) => ({
                ...value,
                [documentRow.id]: {
                    status: "error",
                    message: mapped.message,
                    retryAvailableAt,
                },
            }));
        } finally {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        }
    };

    return (
        <section className="onboarding-section" aria-labelledby="onboarding-documents-title">
            <div className="onboarding-section-heading">
                <div>
                    <p className="onboarding-section-kicker">{t("sections.onboarding.p104")}</p>
                    <h2 id="onboarding-documents-title">{t("sections.onboarding.p61")}</h2>
                </div>
                <span>{documents.length}</span>
            </div>

            {documents.length === 0 ? (
                <div className="onboarding-inline-state onboarding-inline-warning">
                    <FileWarning aria-hidden="true" size={18} />
                    <div>
                        <strong>{t("sections.onboarding.p105")}</strong>
                        <p>{t("sections.onboarding.p106")}</p>
                    </div>
                </div>
            ) : (
                <div className="onboarding-document-list">
                    {documents.map((documentRow) => {
                        const state = downloadState[documentRow.id] || {
                            status: "idle",
                            message: "",
                            retryAvailableAt: null,
                        };
                        const waitSeconds = state.retryAvailableAt
                            ? Math.max(0, Math.ceil((state.retryAvailableAt - now) / 1000))
                            : 0;
                        const waiting = waitSeconds > 0;
                        const loading = state.status === "loading";
                        const typeLabel =
                            DOCUMENT_TYPE_LABELS[documentRow.document_type] ||
                            documentRow.document_type;
                        const statusLabel =
                            DOCUMENT_STATUS_LABELS[documentRow.status] || documentRow.status;

                        return (
                            <article
                                className="onboarding-document-row"
                                key={documentRow.id}
                                aria-busy={loading}
                            >
                                <div className="onboarding-document-icon" aria-hidden="true">
                                    {documentRow.status === "rejected" ? (
                                        <FileWarning size={19} />
                                    ) : (
                                        <FileCheck2 size={19} />
                                    )}
                                </div>
                                <div className="onboarding-document-body">
                                    <div className="onboarding-document-title">
                                        <div>
                                            <h3>{documentRow.original_filename}</h3>
                                            <p>{typeLabel}</p>
                                        </div>
                                        <span
                                            className={`onboarding-status onboarding-status-${
                                                documentRow.status === "rejected"
                                                    ? "danger"
                                                    : documentRow.status === "accepted"
                                                      ? "success"
                                                      : "warning"
                                            }`}
                                        >
                                            {statusLabel}
                                        </span>
                                    </div>
                                    <dl className="onboarding-document-meta">
                                        <div>
                                            <dt>{t("sections.onboarding.p107")}</dt>
                                            <dd>{documentRow.content_type}</dd>
                                        </div>
                                        <div>
                                            <dt>{t("sections.onboarding.p108")}</dt>
                                            <dd>{formatBytes(documentRow.size_bytes)}</dd>
                                        </div>
                                        <div>
                                            <dt>{t("sections.onboarding.p109")}</dt>
                                            <dd>{formatDateTime(documentRow.created_at)}</dd>
                                        </div>
                                        <div>
                                            <dt>SHA-256</dt>
                                            <dd>
                                                <details>
                                                    <summary>{documentRow.sha256.slice(0, 12)}…</summary>
                                                    <code>{documentRow.sha256}</code>
                                                </details>
                                            </dd>
                                        </div>
                                    </dl>
                                    {documentRow.rejection_reason ? (
                                        <p className="onboarding-document-reason">
                                            {documentRow.rejection_reason}
                                        </p>
                                    ) : null}
                                    {state.message ? (
                                        <p className="onboarding-row-error" role="alert">
                                            {state.message}
                                        </p>
                                    ) : null}
                                </div>
                                <button
                                    className="admin-button admin-button-secondary onboarding-download-button"
                                    type="button"
                                    disabled={loading || waiting}
                                    onClick={() => startDownload(documentRow)}
                                    aria-label={
                                        waiting
                                            ? t("sections.onboarding.p110", {v0: waitSeconds, v1: documentRow.original_filename})
                                            : loading
                                              ? t("sections.onboarding.p111", {v0: documentRow.original_filename})
                                              : t("sections.onboarding.p112", {v0: documentRow.original_filename})
                                    }
                                >
                                    {loading ? (
                                        <LoaderCircle aria-hidden="true" size={16} />
                                    ) : (
                                        <Download aria-hidden="true" size={16} />
                                    )}
                                    <span>
                                        {waiting
                                            ? t("sections.onboarding.p113", {v0: waitSeconds})
                                            : loading
                                              ? t("sections.onboarding.p114")
                                              : state.status === "error"
                                                ? t("sections.onboarding.p4")
                                                : t("sections.onboarding.p115")}
                                    </span>
                                </button>
                            </article>
                        );
                    })}
                </div>
            )}
        </section>
    );
};
